import { useCallback, useState, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { RecipientResponse } from '@shared/api';
import { Icon, Modal, NoData, SearchInput, Text } from '@shared/widgets';
import SwapIcon from '@assets/icons/swap.svg';

interface SelectRecipientProps {
  loading?: boolean;
  label: ReactNode;
  items: RecipientResponse[];
  value?: RecipientResponse | null;
  onChange: (value?: RecipientResponse | null) => void;
  searchValue: string;
  onSearch: (value: string) => void;
}

export const SelectRecipient = (props: SelectRecipientProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const onSelect = useCallback(
    (value?: RecipientResponse) => {
      props.onChange(value);
      setOpen(false);
    },
    [props.onChange],
  );

  const title = useMemo(() => props.label ?? t('placeholders.selectRecipient'), [props.label]);
  return (
    <>
      <Text className={cn('mb-3', PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{title}</Text>
      <AccountListItem value={props.value} isShowEndIcon onClick={() => setOpen(true)} />
      <Modal isOpen={open} onClose={() => setOpen(false)} title={t('selectRecipient.modals.addRecipients.title')} width='450px' position='top'>
        <SearchInput className='mb-2' withDebounce value={props.searchValue} onChange={props.onSearch} />
        <div className='flex flex-col gap-2 overflow-y-auto'>
          {props.items.map((item, idx) => (
            <AccountListItem
              key={idx}
              className={cn(PaletteUtils.isInsigniaPalette && 'dark:bg-slate', 'dark:hover:bg-slate/70')}
              value={item}
              isShowEndIcon={false}
              onClick={() => onSelect(item)}
            />
          ))}
          {props.items.length === 0 && <NoData />}
        </div>
      </Modal>
    </>
  );
};

interface AccountListItemProps {
  className?: string;
  value?: RecipientResponse | null;
  isShowEndIcon?: boolean;
  onClick: () => void;
}

const AccountListItem = (props: AccountListItemProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex w-full cursor-pointer items-center rounded-[2px] px-3 transition-colors duration-200',
        'max-[480px]:flex-wrap',
        PaletteUtils.isInsigniaPalette && ['bg-ice dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
        PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
        props.className,
      )}
      onClick={props.onClick}
    >
      <div className={cn('mr-2 flex w-full cursor-pointer bg-transparent py-3 pr-3', props.isShowEndIcon && 'w-auto')}>
        {props.value === undefined && (
          <div className='flex items-center gap-2'>
            <div
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-full border-2 border-dashed border-primary',
                PaletteUtils.isVcfcPalette && 'dark:border-white',
              )}
            >
              <Text className='m-auto' weight='semibold' size='3xl' color='primary'>
                +
              </Text>
            </div>
            <Text weight='semibold'>{t('placeholders.selectRecipient')}</Text>
          </div>
        )}
        {props.value && (
          <div className='item-center flex gap-2'>
            <div className='g flex h-11 min-h-11 w-11 min-w-11 rounded-full bg-primary'>
              <Text className='m-auto' weight='semibold' color='white'>
                {FormatUtils.getRecipientInitials(props.value)}
              </Text>
            </div>
            <div className='flex h-full w-full flex-col justify-center'>
              <Text weight='semibold'>{FormatUtils.getRecipientName(props.value)}</Text>
              <Text weight='semibold' size='xs' color='gray'>
                {props.value.accountNumber || props.value.iban || '-'}
              </Text>
            </div>
          </div>
        )}
      </div>
      {props.value?.currency && (
        <div className='flex h-7 w-16 min-w-16 max-w-16 items-center justify-center rounded-[5px] border border-primary p-2'>
          <Text className='mr-2' weight='semibold' size='xs' color='primary'>
            {CurrencyUtils.getSymbolByCode(props.value.currency)}
          </Text>
          <Text uppercase weight='semibold' size='xs' color='primary'>
            {props.value.currency}
          </Text>
        </div>
      )}
      {props.isShowEndIcon && (
        <div className='my-auto pl-4'>
          <Icon
            icon={<SwapIcon width='24' height='24' className={cn(PaletteUtils.isVcfcPalette && 'text-pearl-dark-gray')} />}
            className={cn('rotate-90', PaletteUtils.isVcfcPalette && 'mr-5 scale-x-[-1]')}
          />
        </div>
      )}
    </div>
  );
};
